import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';

const { FiChevronLeft, FiChevronRight } = FiIcons;

// Cache-Buster
const addCacheBuster = (path) => {
  if (!path) return path;
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}t=${Date.now()}`;
};

// Slide-Konfiguration - PFADE HIER ÄNDERN
const SLIDE_CONFIG = [
  { id: 1, path: '/images/mohrueben.webp', link: '/marknader', buttonClass: 'btn-custom-primary' },
  { id: 2, path: '/images/kaal.webp', link: '/produkter', buttonClass: 'bg-accent-green hover:bg-green-600' },
  { id: 3, path: '/images/mejeriet2.webp', link: '/bilder', buttonClass: 'btn-custom-primary' }
];

const HeroCarousel = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [refreshKey, setRefreshKey] = useState(Date.now());

  // Slides mit Cache-Buster
  const slides = useMemo(() => SLIDE_CONFIG.map(s => ({
    ...s,
    title: s.id === 1 ? t('hero.ecological') : s.id === 2 ? t('hero.tasty') : t('hero.local'),
    image: addCacheBuster(s.path),
    buttonText: s.id === 1 ? t('hero.marketsBtn') : s.id === 2 ? t('hero.productsBtn') : t('hero.imagesBtn')
  })), [t, refreshKey]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="relative hero-slide overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.7 }}
          className="hero-slide flex items-center justify-center relative"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${slides[currentSlide].image})`,
          }}
        >
          <div className="text-center text-white z-10 px-4">
            <motion.h1
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-5xl md:text-6xl font-bold mb-8"
            >
              {slides[currentSlide].title}
            </motion.h1>
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              <Link
                to={slides[currentSlide].link}
                className={`inline-block px-8 py-3 rounded-lg font-semibold text-lg transition-all duration-300 ${slides[currentSlide].buttonClass} text-white`}
              >
                {slides[currentSlide].buttonText}
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-all duration-300"
      >
        <SafeIcon icon={FiChevronLeft} className="w-6 h-6" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white p-3 rounded-full transition-all duration-300"
      >
        <SafeIcon icon={FiChevronRight} className="w-6 h-6" />
      </button>

      {/* Indicators */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide ? 'bg-white' : 'bg-white/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroCarousel;